import { isEmpty } from 'lodash-es'
import { PropType, computed, defineComponent, nextTick, ref } from 'vue'

import { CsGallery, CsSecondaryButton, MsHeadline } from '@sunrise/components'
import { MsImage, MsThreeSixtyDegree } from '@sunrise/icons'
import { screenSizeDetection, useGlobalStore } from '@sunrise/shared'
import { MLM, TrackingParameter, track } from '@sunrise/tracking'
import { Components } from '@sunrise/types'

import { CarouselLabel } from '../../ts/mapper/decoratedEstateDetailMapper.enum'
import { GalleryImages } from '../../ts/mapper/decoratedEstateDetailMapper.interface'

import MsDetailThreeSixtyLightbox from '../MsDetailThreeSixtyLightbox/MsDetailThreeSixtyLightbox.vue'
import MsLikeButton from '../MsLikeButton/MsLikeButton.vue'
import { buildMlmDataOpenGallery, buildTealiumTracking, buildTealiumTracking360Click } from './MsDetailGalleryTracking'
import MsFallBack from './global-fallback-real-estate.svg?component'

export default defineComponent({
    name: 'MsDetailGallery',
    components: {
        CsSecondaryButton,
        CsGallery,
        MsDetailThreeSixtyLightbox,
        MsHeadline,
        MsLikeButton,
        MsFallBack,
        MsImage,
        MsThreeSixtyDegree,
    },
    props: {
        exposeeId: {
            type: Number,
            required: true,
        },
        images: {
            type: Object as PropType<GalleryImages>,
        },
        componentId: {
            type: String,
            required: true,
        },
        threeSixtyViewUrl: {
            type: String,
            default: null,
        },
        galleryAds: {
            type: Array as PropType<Components.CsGallery.GalleryAd[]>,
            default: () => [],
        },
    },
    setup(props) {
        const requestContext = useGlobalStore().requestContext
        const currentUrl = requestContext?.url.href || ''
        const { isScreenL } = screenSizeDetection()
        const initialSlide = ref<number>(0)
        const galleryVisible = ref<boolean>(false)
        const galleryIndex = ref<number>(0)
        const threeSixtyLightboxIsVisible = ref<boolean>(false)
        const galleryTracking = buildTealiumTracking(props.exposeeId)
        const hasImages = computed(() => !isEmpty(props.images))
        const showImageGrid = computed(
            () => props.images && props.images.grid && props.images.grid.length > 2 && isScreenL.value,
        )

        const badgeText = computed(
            () => `${props.images?.gallery.length} ${props.images?.gallery.length === 1 ? 'Bild' : 'Bilder'}`,
        )

        function openThreeSixtyLightbox(exposeeId: number): void {
            threeSixtyLightboxIsVisible.value = true

            const tealiumData: TrackingParameter = buildTealiumTracking360Click(exposeeId)
            track(tealiumData)
        }

        function closeThreeSixtyLightbox() {
            threeSixtyLightboxIsVisible.value = false
        }

        function openGallery(index: number): void {
            galleryIndex.value = index
            nextTick(() => {
                galleryVisible.value = true
            })
            const mlmData = buildMlmDataOpenGallery(props.componentId, props.exposeeId, currentUrl)
            MLM.trackClick(mlmData)
        }

        return {
            badgeText,
            galleryVisible,
            hasImages,
            initialSlide,
            showImageGrid,
            threeSixtyLightboxIsVisible,
            galleryTracking,
            closeThreeSixtyLightbox,
            openThreeSixtyLightbox,
            CarouselLabel,
            openGallery,
            galleryIndex,
        }
    },
})
